import { useCallback, useEffect, useRef, useState } from 'react';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import { fromLonLat } from 'ol/proj';
import FlowLine from 'ol-ext/style/FlowLine';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import { getCenter } from 'ol/extent';
import { fetchPopulationRouteInfo } from 'services/lab2/populationService';

const formatNumber = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) {
        return value ?? '0';
    }
    return number.toLocaleString();
};

const formatItemValue = (value, unit, ratio) => {
    return `${formatNumber(value)} ${unit}${ratio ? ` (${ratio}%)` : ''}`;
};

const useMoveRouteLayer = ({
    mapRef,
    mapLayerName,
    selectedAge,
    moveRouteOverlayRef,
    setLineLayerVisible,
    setSelectedMoveRouteInfo,
    setSelectedMoveRouteInfoItems,
    resetMoveRouteInfoItems,
    setMovPopParams,
}) => {
    const lineLayerRef = useRef(null);
    const requestIdRef = useRef(0);
    const animationPhaseRef = useRef(0);
    const animationFrameRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const ensureLineLayer = useCallback(() => {
        const map = mapRef.current;
        if (!map) return null;

        if (lineLayerRef.current) {
            return lineLayerRef.current;
        }

        const existingLayer = map
            .getLayers()
            .getArray()
            .find((layer) => layer.get('name') === 'lineLayer');

        if (existingLayer) {
            lineLayerRef.current = existingLayer;
            return existingLayer;
        }

        const lineLayer = new VectorLayer({
            name: 'lineLayer',
            source: new VectorSource(),
        });
        lineLayer.setZIndex(40);
        map.addLayer(lineLayer);
        lineLayerRef.current = lineLayer;
        return lineLayer;
    }, [mapRef]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return undefined;

        const handlePointerMove = (evt) => {
            if (evt.dragging) return;
            const pixel = map.getEventPixel(evt.originalEvent);
            const feature = map.forEachFeatureAtPixel(pixel, (f) => f);
            if (feature && feature.getGeometry().getType().toLowerCase() === 'linestring') {
                if (moveRouteOverlayRef.current?.getPosition() !== undefined) {
                    return;
                }
                const coord = getCenter(feature.getGeometry().getExtent());
                moveRouteOverlayRef.current?.setPosition(coord);
            }
        };

        map.on('pointermove', handlePointerMove);
        return () => {
            map.un('pointermove', handlePointerMove);
        };
    }, [mapRef, moveRouteOverlayRef]);

    useEffect(() => {
        const map = mapRef.current;
        return () => {
            if (map && lineLayerRef.current) {
                map.removeLayer(lineLayerRef.current);
            }
            lineLayerRef.current = null;
        };
    }, [mapRef]);

    const clearMoveRoutes = useCallback(({ preserveParams = false } = {}) => {
        const lineLayer = lineLayerRef.current;
        if (lineLayer) {
            lineLayer.getSource().clear();
        }
        setSelectedMoveRouteInfo(null);
        resetMoveRouteInfoItems();
        moveRouteOverlayRef.current?.setPosition(undefined);
        setLineLayerVisible(false);
        if (!preserveParams) {
            setMovPopParams?.(null);
        }
    }, [
        moveRouteOverlayRef,
        resetMoveRouteInfoItems,
        setLineLayerVisible,
        setMovPopParams,
        setSelectedMoveRouteInfo,
    ]);

    useEffect(() => {
        const animate = () => {
            animationPhaseRef.current = (animationPhaseRef.current + 0.01) % 1;
            if (lineLayerRef.current) {
                lineLayerRef.current.changed();
            }
            animationFrameRef.current = requestAnimationFrame(animate);
        };
        animationFrameRef.current = requestAnimationFrame(animate);
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    const drawMoveRoutes = useCallback(
        async ({ feature, params }) => {
            if (!feature || !params) return;

            const map = mapRef.current;
            const lineLayer = ensureLineLayer();
            if (!map || !lineLayer) {
                return;
            }

            const requestId = ++requestIdRef.current;
            setIsLoading(true);
            setError(null);

            try {
                const geojsonData = await fetchPopulationRouteInfo({
                    layerName: mapLayerName,
                    hjdCd: feature.get('hjd_cd'),
                    count: params.cnt,
                    direction: params.direction,
                    age: selectedAge,
                    houseType: params.houseType,
                    type: params.type,
                });

                if (requestIdRef.current !== requestId) {
                    return;
                }

                const records = geojsonData?.features ?? [];
                if (records.length === 0) {
                    clearMoveRoutes();
                    return;
                }

                const isPopulationType = params.type?.includes('pop');
                const isOutDirection = params.direction?.includes('out');
                const firstRecord = records[0]?.properties || {};

                if (isPopulationType) {
                    setSelectedMoveRouteInfo({
                        name: isOutDirection ? firstRecord.out_hjd_nm : firstRecord.in_hjd_nm,
                        value: formatItemValue(firstRecord.in_cnt, '명', firstRecord.in_ratio),
                    });
                } else {
                    setSelectedMoveRouteInfo({
                        name: isOutDirection ? firstRecord.out_hjd_nm : firstRecord.in_hjd_nm,
                        value: formatItemValue(firstRecord.in_cnt, '가구', firstRecord.in_house_ratio),
                    });
                }

                const moveRouteItems = records.map((record) => {
                    const props = record.properties || {};
                    if (isPopulationType) {
                        if (isOutDirection) {
                            return {
                                rank: record.id,
                                outNm: props.in_hjd_nm,
                                outCnt: formatItemValue(props.cnt, '명', props.pop_ratio),
                                inNm: props.out_hjd_nm2,
                                inCnt: formatItemValue(props.cnt2, '명', props.pop_ratio2),
                            };
                        }
                        return {
                            rank: record.id,
                            outNm: props.in_hjd_nm2,
                            outCnt: formatItemValue(props.cnt2, '명', props.pop_ratio2),
                            inNm: props.out_hjd_nm,
                            inCnt: formatItemValue(props.cnt, '명', props.pop_ratio),
                        };
                    }

                    if (isOutDirection) {
                        return {
                            rank: record.id,
                            outNm: props.in_hjd_nm,
                            outCnt: formatItemValue(props.cnt, '가구', props.house_ratio),
                            inNm: props.out_hjd_nm2,
                            inCnt: formatItemValue(props.cnt2, '가구', props.house_ratio2),
                        };
                    }
                    return {
                        rank: record.id,
                        outNm: props.in_hjd_nm2,
                        outCnt: formatItemValue(props.cnt2, '가구', props.house_ratio2),
                        inNm: props.out_hjd_nm,
                        inCnt: formatItemValue(props.cnt, '가구', props.house_ratio),
                    };
                });

                setSelectedMoveRouteInfoItems(moveRouteItems);

                const features = records
                    .map((record) => {
                        const props = record.properties || {};
                        const targetCoordinates =
                            record.the_geom?.coordinates || record.geometry?.coordinates;
                        if (!targetCoordinates) {
                            return null;
                        }
                        const destination = fromLonLat(targetCoordinates);
                        const fromCoord = isOutDirection
                            ? feature.getGeometry().getCoordinates()
                            : destination;
                        const toCoord = isOutDirection
                            ? destination
                            : feature.getGeometry().getCoordinates();
                        const [adjustedFrom, adjustedTo] = shortenLineSegment(
                            fromCoord,
                            toCoord,
                            0.025,
                            0.05
                        );

                        const flowFeature = new Feature({
                            geometry: new LineString([adjustedFrom, adjustedTo]),
                        });
                        const styles = buildFlowStyles(props, isOutDirection, animationPhaseRef);
                        flowFeature.setStyle(styles);
                        flowFeature.setProperties(props);
                        return flowFeature;
                    })
                    .filter(Boolean);

                lineLayer.setSource(
                    new VectorSource({
                        features,
                    })
                );
                setLineLayerVisible(true);
            } catch (err) {
                console.error('Failed to draw move routes', err);
                if (requestIdRef.current === requestId) {
                    setError(err);
                }
            } finally {
                if (requestIdRef.current === requestId) {
                    setIsLoading(false);
                }
            }
        },
        [
            clearMoveRoutes,
            ensureLineLayer,
            mapLayerName,
            mapRef,
            selectedAge,
            setLineLayerVisible,
            setSelectedMoveRouteInfo,
            setSelectedMoveRouteInfoItems,
        ]
    );

    return { drawMoveRoutes, clearMoveRoutes, isLoading, error };
};

export default useMoveRouteLayer;

const buildFlowStyles = (props, isOutDirection, animationPhaseRef) => {
    const countValue = Number(props.cnt ?? props.cnt2 ?? 0);
    const baseWidth = Math.min(6 + countValue / 1500, 20);
    const windowLength = 0.4;
    const gradient = isOutDirection
        ? ['#7f0000', '#b71c1c', '#ff5252']
        : ['#002171', '#0d47a1', '#1e88e5'];
    const colorFn = (feature, pos) => {
        const phase = animationPhaseRef.current;
        const factor = getSegmentFactor(pos, phase, windowLength);
        if (factor <= 0) return 'rgba(0,0,0,0)';
        const idx = Math.min(gradient.length - 1, Math.floor(factor * gradient.length));
        return hexToRgba(gradient[idx], 0.25 + factor * 0.55);
    };
    const baseLineStyle = new Style({
        stroke: new Stroke({
            color: hexToRgba(isOutDirection ? '#f06a6a' : '#5e8de6', 0.65),
            width: Math.max(2, baseWidth * 0.34),
            lineCap: 'round',
            lineJoin: 'round',
        }),
        zIndex: 5,
    });
    const glowStyle = new FlowLine({
        width: (feature, pos) => {
            const phase = animationPhaseRef.current;
            const factor = getSegmentFactor(pos, phase, windowLength);
            if (factor <= 0) return 0;
            return (baseWidth + 0.1) * factor;
        },
        color: (feature, pos) => {
            const phase = animationPhaseRef.current;
            const factor = getSegmentFactor(pos, phase, windowLength);
            if (factor <= 0) return 'rgba(0,0,0,0)';
            const hue = gradient[gradient.length - 1];
            return hexToRgba(hue, 0.02 + factor * 0.18);
        },
        arrow: 0,
        lineCap: 'round',
        visible: true,
    });
    const trailStyle = new FlowLine({
        width: (feature, pos) => {
            const phase = animationPhaseRef.current;
            const factor = getSegmentFactor(pos, phase, windowLength);
            if (factor <= 0) return 0;
            return baseWidth * 0.55 * (0.2 + factor * 0.8);
        },
        color: (feature, pos) => {
            const phase = animationPhaseRef.current;
            const factor = getSegmentFactor(pos, phase, windowLength);
            if (factor <= 0) return 'rgba(0,0,0,0)';
            return hexToRgba(gradient[0], 0.08 + factor * 0.45);
        },
        lineCap: 'round',
        arrow: 0,
        offset0: isOutDirection ? 11 : 6,
        offset1: isOutDirection ? 4 : 13,
    });
    const mainStyle = new FlowLine({
        width: (feature, pos) => {
            const phase = animationPhaseRef.current;
            const factor = getSegmentFactor(pos, phase, windowLength);
            if (factor <= 0) return 0;
            const eased = Math.sin(Math.min(1, factor) * Math.PI);
            const pulse = 0.06 * Math.sin(factor * Math.PI * 2);
            return baseWidth * (0.2 + eased * 0.55 + pulse);
        },
        width2: baseWidth * 0.38,
        color: colorFn,
        color2: gradient[gradient.length - 1],
        arrow: 1,
        arrowSize: Math.max(22, baseWidth * 1.1),
        arrowColor: gradient[gradient.length - 1],
        lineCap: 'round',
        offset0: isOutDirection ? 7 : 3,
        offset1: isOutDirection ? 3 : 9,
    });
    const sparkStyle = new FlowLine({
        width: (feature, pos) => {
            const phase = animationPhaseRef.current;
            const factor = getSegmentFactor(pos, phase, windowLength);
            if (factor <= 0) return 0;
            return baseWidth * 0.28 * factor;
        },
        width2: baseWidth * 0.16,
        color: (feature, pos) => {
            const phase = animationPhaseRef.current;
            const factor = getSegmentFactor(pos, phase, windowLength);
            if (factor <= 0) return 'rgba(0,0,0,0)';
            return hexToRgba(gradient[gradient.length - 1], 0.25 + factor * 0.6);
        },
        color2: gradient[gradient.length - 1],
        arrow: 1,
        arrowSize: Math.max(30, baseWidth * 1.5),
        arrowColor: gradient[gradient.length - 1],
        lineCap: 'round',
        offset0: isOutDirection ? 2 : 6,
        offset1: isOutDirection ? 0.8 : 5,
    });
    return [baseLineStyle, glowStyle, trailStyle, mainStyle, sparkStyle];
};

const hexToRgba = (hex, alpha = 1) => {
    const normalized = hex.replace('#', '');
    const bigint = parseInt(normalized, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const shortenLineSegment = (fromCoord, toCoord, startRatio = 0.08, endRatio = 0.12) => {
    const clampedStart = Math.max(0, Math.min(startRatio, 0.45));
    const maxEnd = Math.max(0, 0.95 - clampedStart);
    const clampedEnd = Math.max(0, Math.min(endRatio, Math.min(maxEnd, 0.45)));
    const dx = toCoord[0] - fromCoord[0];
    const dy = toCoord[1] - fromCoord[1];
    const adjustedStart = [
        fromCoord[0] + dx * clampedStart,
        fromCoord[1] + dy * clampedStart,
    ];
    const adjustedEnd = [
        toCoord[0] - dx * clampedEnd,
        toCoord[1] - dy * clampedEnd,
    ];
    return [adjustedStart, adjustedEnd];
};

const getSegmentFactor = (pos, phase, windowLength = 0.4) => {
    const relative = (pos - phase + 1) % 1;
    if (relative > windowLength) {
        return 0;
    }
    return relative / windowLength;
};
